import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store';
import { BrowserRouter } from 'react-router-dom';
import PrivateRouter from './components/layouts/PrivateRouter';
import { ROUTES_PRIVATE, ROUTES_SUPER_ADMIN } from 'utils/routes';
import { showAlert } from 'reducer/rootReducer';
import { useTranslation } from 'react-i18next';
import { ALERT_TYPE, TKeys, USER_ROLE } from 'utils/enums';
import LoginPage from 'components/pages/login/LoginPage';
import ResetPasswordPage from 'components/pages/reset-password/ResetPasswordPage';
import { setUser } from 'reducer/userReducer';
import { changeI18nLocale, getLocalStorageValue } from 'utils/helpers';

const AppBrowserRouter = () => {
    const userData = getLocalStorageValue('authParams');
    const user = useSelector((state: RootState) => state.user.user);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const authorities = userData?.user?.authorities ?? [];
    const pathname = window.location.pathname;
    const excludeCheckPathName = ['/', '/login'];

    const isCheckPathName = !excludeCheckPathName.includes(pathname);

    const isUserRole99 = `${userData?.user?.role}` === `${USER_ROLE.MASTER_ADMIN}`;

    const isValidUrlWithAuthorities = Object.values(ROUTES_PRIVATE).some(
        ({ ID, PATH, CHILDREN }: IMenuItem) => {
            return (
                (!ID || authorities.includes(ID)) &&
                (pathname === PATH ||
                    Object.values(CHILDREN ?? {}).some(({ PATH }) => pathname === PATH))
            );
        },
    );

    const isValidUrlWithRole99 = Object.values(ROUTES_SUPER_ADMIN).some(
        ({ PATH, CHILDREN }: IMenuItem) => {
            return (
                pathname === PATH ||
                Object.values(CHILDREN ?? {}).some(({ PATH }) => pathname === PATH)
            );
        },
    );

    const isValidUrl = isUserRole99 ? isValidUrlWithRole99 : isValidUrlWithAuthorities;

    if (isCheckPathName && !isValidUrl && user && !user?.is_new) {
        dispatch(
            showAlert({
                status: true,
                message: t(TKeys.MSE00029),
                type: ALERT_TYPE.ERROR,
            }),
        );
    }

    useEffect(() => {
        const reloadUser = async () => {
            if (userData && userData.user) {
                dispatch(setUser(userData.user));
                await changeI18nLocale(userData?.user?.language);
            }
        };

        reloadUser();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const renderRouter = () => {
        if (!user && !userData?.user) return <LoginPage />;

        if (user && user.is_new && userData?.user?.is_new) return <ResetPasswordPage />;

        if (!isValidUrl && isCheckPathName) return <LoginPage />;

        return <PrivateRouter />;
    };

    return <BrowserRouter>{renderRouter()}</BrowserRouter>;
};

export default AppBrowserRouter;
